import "./fonts.scss";
import "core-theme/normalize";
import { setMediaAxiosInstance } from "@b2bportal/core-media";
import { datadogRum } from "@datadog/browser-rum";
import { axiosInstance } from "@hopper-b2b/api";
import { initializeRum, initializeLogs } from "@hopper-b2b/datadog";
import { serviceWorker } from "@hopper-b2b/portal";
import { ClientName } from "@hopper-b2b/types";
import { createRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";
import config, { environmentConfig } from "../config";
import { App } from "./App";
import { startMocks } from "./dev/wallet-mocks/startMocks";
// HTS Media API calls share the app axios instance (interceptors, headers).
setMediaAxiosInstance(axiosInstance);

const sessionIdKey = "sessionId";
const sessionId = sessionStorage.getItem(sessionIdKey)
  ? sessionStorage.getItem(sessionIdKey)
  : uuidv4();
sessionStorage.setItem(sessionIdKey, sessionId);
const storeDomain = "rbc.com";

// Initialize Datadog only once, after a small delay to ensure the bundle is loaded
let datadogInitialized = false;

const initializeDatadog = () => {
  if (datadogInitialized) return;
  datadogInitialized = true;

  try {
    initializeLogs({
      service: ClientName.RBC,
      env: config.bootConfig.env,
      sessionId,
      version: environmentConfig.version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error", "info", "log"],
    });

    if (config.bootConfig.env !== "development") {
      initializeRum({
        enableSessionRecording: true,
        service: ClientName.RBC,
        env: config.bootConfig.env,
        version: environmentConfig.version,
        allowedTracingUrls: [
          "https://rbc.eng.app-development.h4r.io/",
          "https://rbc.staging.hopper.com/",
          "https://booktravel.steavionrewards.com/",
          "https://rbc.portal.hopper.com/",
          "https://booktravel.avionrewards.com/",
          "https://rbc-concierge.portal.hopper.com/",
          "https://rbc-internal.portal.staging.hopper.com/",
        ],
        defaultPrivacyLevel: "mask",
        trackUserInteractions: false,
        sessionReplaySampleRate: config.bootConfig.env === "staging" ? 100 : 30,
      });

      // Log RUM context after initialization
      setTimeout(() => {
        window.DD_LOGS?.logger?.info?.(
          `DD RUM: ${JSON.stringify(datadogRum.getInternalContext())}`
        );
      }, 1000);
    }
  } catch (e) {
    console.error("failed to initialize datadog", e);
    datadogInitialized = false;
  }
};

// Initialize Datadog after a delay
setTimeout(initializeDatadog, 100);

// Riskified
const riskifiedScript = document.createElement("script");
riskifiedScript.src =
  ("https:" === document.location.protocol ? "https://" : "http://") +
  "beacon.riskified.com?shop=" +
  storeDomain +
  "&sid=" +
  sessionId;
riskifiedScript.async = true;
riskifiedScript.type = "text/javascript";
document.head.appendChild(riskifiedScript);
const container = document.getElementById("root");
const root = createRoot(container);

// Reload when Vite can't load a lazy chunk, which usually means stale HTML
// after a deploy. Rate limited so a chunk that keeps failing can't spin the
// page in a reload loop; once refused, the error falls through to the
// ErrorBoundary in App.tsx and the user lands on the homepage.
const CHUNK_RELOAD_KEY = "rbc-chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

// Vite fires this once per failed dependency, plus once for the module.
let reloading = false;

window.addEventListener("vite:preloadError", (event) => {
  if (reloading) {
    event.preventDefault();
    return;
  }

  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  reloading = true;
  event.preventDefault();
  window.location.reload();
});

// TODO(rbc-travel-wallet): remove after BE is set up
// Start the mock worker (no-op unless mock mode is enabled) before rendering so
// mocked endpoints are intercepted from the first request. Render regardless of
// whether mock startup succeeds.
startMocks().finally(() => root.render(<App />));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
